import * as RongIMLib from '@rongcloud/imlib-next'
import { yxToast } from './toast'
import { env } from './env'
import { isFunction } from './type'
import { getChatRoomToken, joinChatRoom } from '@/service/teach-server'

const { Events, ConversationType, MessageType } = RongIMLib
const printLog = !env.isPro
const eventMap = new Map()
const RIM_CONFIG = {
    appKey: '', // App Key
    token: '' // 用户令牌
}

/**
 * 打印调试日志
 * @param text
 * @param data
 */
function consoleLog(text, data) {
    if (printLog) {
        if (text && data) console.log(text, data)
        else if (text) console.log(text)
    }
}

/**
 * 执行监听器会调
 * @param event
 * @param data
 */
function execListenerCallback(event, data) {
    const fns = eventMap.get(event)
    if (!fns || !fns.length) return
    fns.forEach(fn => fn.call(RIM, data))
}

/**
 * 调用融云 api
 * @param RimApiPromise
 * @returns {Promise<unknown>}
 */
async function callRimApi(RimApiPromise) {
    try {
        const { code, data } = await RimApiPromise
        if (code === 0) return Promise.resolve(data)
        else return Promise.reject(data)
    } catch (e) {
        return Promise.reject(e)
    }
}

// 融云 im
export const RIM = {
    connected: false,
    initialized: false,
    Events,
    MessageType,
    ConversationType,

    get config() {
        return { ...RIM_CONFIG }
    },
    /**
     * 从服务端获取 appkey 和 token
     */
    async getConfigHttp() {
        const { appKey, token } = await getChatRoomToken()
        this.setConfig({ appKey, token })
    },
    /**
     * 设置 RIM 配置
     * @param options.appKey
     * @param options.token
     */
    setConfig(options = {}) {
        Object.keys(options).forEach(key => (RIM_CONFIG[key] = options[key]))
    },
    /**
     * 初始化
     */
    init() {
        if (this.initialized) return
        if (!RIM_CONFIG.appKey) throw '请设置融云 appKey'

        RongIMLib.init({ appkey: RIM_CONFIG.appKey })
        this.initialized = true

        // 正在链接的事件状态
        RongIMLib.addEventListener(Events.CONNECTING, function(connecting) {
            execListenerCallback(Events.CONNECTING, connecting)
            consoleLog('链接中', connecting)
        })

        // 链接到服务器会触发这个事件
        RongIMLib.addEventListener(Events.CONNECTED, function(connected) {
            execListenerCallback(Events.CONNECTED, connected)
            consoleLog('已链接', connected)
        })

        // 手动调用 disconnect 方法或者用户被踢下线 会触发这个事件
        // 连接中断，需要业务层进行重连处理
        RongIMLib.addEventListener(Events.DISCONNECT, function(disconnect) {
            execListenerCallback(Events.DISCONNECT, disconnect)
            consoleLog('已断开', disconnect)
        })

        // 链接出问题时，内部进行重新链接，会出发这个事件
        // 链接中断，SDK 会尝试重连，业务层无需关心
        RongIMLib.addEventListener(Events.SUSPEND, function(suspend) {
            execListenerCallback(Events.SUSPEND, suspend)
            consoleLog('链接异常断开(自动重连)', suspend)
        })

        RongIMLib.addEventListener(Events.MESSAGES, function(messages) {
            execListenerCallback(Events.MESSAGES, messages)
            consoleLog('收到消息', messages)
        })

        RongIMLib.addEventListener(Events.READ_RECEIPT_RECEIVED, function(readReceiptReceived) {
            execListenerCallback(Events.READ_RECEIPT_RECEIVED, readReceiptReceived)
            consoleLog('收到已读回执（单聊）', readReceiptReceived)
        })

        RongIMLib.addEventListener(Events.MESSAGE_RECEIPT_REQUEST, function(messageReceiptRequest) {
            execListenerCallback(Events.MESSAGE_RECEIPT_REQUEST, messageReceiptRequest)
            consoleLog('收到已读回执请求（群聊）', messageReceiptRequest)
        })

        RongIMLib.addEventListener(Events.MESSAGE_RECEIPT_RESPONSE, function(messageReceiptResponse) {
            execListenerCallback(Events.MESSAGE_RECEIPT_REQUEST, messageReceiptResponse)
            consoleLog('收到已读回执响应（群聊）', messageReceiptResponse)
        })

        RongIMLib.addEventListener(Events.CONVERSATION, function(conversation) {
            execListenerCallback(Events.CONVERSATION, conversation)
            consoleLog('收到会话变更通知', conversation)
        })

        RongIMLib.addEventListener(Events.CHATROOM, function(chatroom) {
            execListenerCallback(Events.CHATROOM, chatroom)
            consoleLog('收到聊天室变更通知', chatroom)
        })

        RongIMLib.addEventListener(Events.EXPANSION, function(expansion) {
            execListenerCallback(Events.EXPANSION, expansion)
            consoleLog('收到扩展变更通知', expansion)
        })

        RongIMLib.addEventListener(Events.PULL_OFFLINE_MESSAGE_FINISHED, function(pullOfflineMessageFinished) {
            execListenerCallback(Events.PULL_OFFLINE_MESSAGE_FINISHED, pullOfflineMessageFinished)
            consoleLog('离线消息拉取完成', pullOfflineMessageFinished)
        })

        RongIMLib.addEventListener(Events.TAG, function(tag) {
            execListenerCallback(Events.TAG, tag)
            consoleLog('收到 tag 变动通知', tag)
        })

        RongIMLib.addEventListener(Events.CONVERSATION_TAG, function(conversationTag) {
            execListenerCallback(Events.MESSAGE_RECEIPT_REQUEST, conversationTag)
            consoleLog('收到会话 tag 变更通知', conversationTag)
        })

        RongIMLib.addEventListener(Events.TYPING_STATUS, function(typingStatus) {
            execListenerCallback(Events.MESSAGE_RECEIPT_REQUEST, typingStatus)
            consoleLog('收到正在输入状态通知', typingStatus)
        })
    },
    /**
     * 注册事件
     * @param event 参考Events
     * @param fn 事件会调
     */
    watch(event, fn) {
        if (!event) {
            yxToast('请填写事件名')
            return
        }

        if (!isFunction(fn)) {
            yxToast('fn 必须是函数')
            return
        }

        eventMap.set(event, [...(eventMap.get(event) || []), fn])
    },
    /**
     * 取消监听
     * @param event
     * @param fn
     */
    unwatch(event, fn) {
        if (!event) {
            yxToast('请填写事件名')
            return
        }
        if (fn && !isFunction(fn)) {
            yxToast('fn 必须是函数')
            return
        }

        const fns = eventMap.get(event)
        if (fns && fns.length) {
            const index = fns.indexOf(fn)
            if (index > -1) {
                fns.splice(index, 1)
            }
        }
    },
    /**
     * 连接聊天服务器
     * @returns {Promise<unknown>}
     */
    connect() {
        if (this.connected) return null
        if (!this.initialized) this.init()
        if (!RIM_CONFIG.token) throw '请配置融云 token'

        return new Promise((resolve, reject) => {
            RongIMLib.connect(RIM_CONFIG.token)
                .then(res => {
                    if (res.code === 0) {
                        this.connected = true
                        consoleLog('[CONNECT]' + res.data.userId)
                    }
                    resolve(res)
                })
                .catch(reject)
        })
    },
    /**
     * 断开连接
     * 适用场景: 注销账号，切换账号时，推荐使用此方法
     * @returns {Promise<unknown>}
     */
    disconnect() {
        return new Promise((resolve, reject) => {
            RongIMLib.disconnect()
                .then(() => {
                    consoleLog('成功断开')
                    this.connected = false
                    this.initialized = false
                    resolve()
                })
                .catch(reject)
        })
    },
    /**
     * 设置更新拓展信息
     * @param expansion
     * @param message
     * @returns {Promise<unknown>}
     */
    async updateExpansion(expansion, message) {
        return callRimApi(RongIMLib.updateMessageExpansion(expansion, message))
    },
    /**
     * 删除拓展信息
     * @param keyArray
     * @param message
     * @returns {Promise<unknown>}
     */
    async removeExpansion(keyArray, message) {
        return callRimApi(RongIMLib.removeMessageExpansionForKey(keyArray, message))
    },
    /**
     * 加入群组
     */
    async joinGroup({ groupId, groupName }) {
        return joinChatRoom({
            chatGroupId: groupId,
            chatGroupName: groupName,
            confirmRulesStatus: 1,
            confirmTermsStatus: 1
        })
    }
}

// 默认当前用户字段
const defaultUserKeys = () => ({
    userId: '',
    name: '',
    portraitUri: ''
})

// 群组管理
export class GroupManage {
    groupId = 0 // 群组id
    groupName = '' // 群组名称
    messageUser = defaultUserKeys() // 当前用户

    /**
     * 回话信息
     * @returns {{conversationType: ConversationType.GROUP, targetId: number}}
     */
    get conversation() {
        return {
            conversationType: ConversationType.GROUP,
            targetId: this.groupId
        }
    }

    constructor({ groupId, groupName, messageUser } = {}) {
        if (!groupId) {
            yxToast('请传入群组id')
            throw '请传入群组id'
        }
        this.groupId = groupId || 0
        this.groupName = groupName || ''
        this.messageUser = messageUser || defaultUserKeys()
    }

    /**
     * 发送消息
     * 文档地址： https://doc.rongcloud.cn/im/Web/5.X/noui/message/msgsend
     * 返回字段说明： https://www.rongcloud.cn/docs/api/js/imlib/v5/interfaces/IAReceivedMessage.html
     * @param message
     * @param options
     * @returns {Promise<unknown>}
     */
    async sendMessage(message, options) {
        return callRimApi(RongIMLib.sendMessage(this.conversation, message, { canIncludeExpansion: true, ...options }))
    }

    /**
     * 发送文本消息
     * @param content
     * @returns {Promise<unknown>}
     */
    sendText({ content } = {}) {
        if (!content) {
            yxToast('content 必填')
            return Promise.reject()
        }
        return this.sendMessage(new RongIMLib.TextMessage({ content, user: this.messageUser }))
    }

    /**
     * 发送图片消息
     * @param imageUri
     * @param extra
     * @returns {Promise<never>|Promise<unknown>}
     */
    sendImage({ imageUri, extra }) {
        if (!imageUri) {
            yxToast('imageUri 必填')
            return Promise.reject()
        }

        return this.sendMessage(
            new RongIMLib.ImageMessage({
                user: this.messageUser,
                content: '', // 图片缩略图，应为 Base64 字符串，且不可超过 80KB
                imageUri, // 图片的远程访问地址
                extra
            })
        )
    }

    /**
     * 发送引用消息
     * @param content
     * @param referMessage
     * @returns {Promise<unknown>}
     */
    sendReference({ content, referMessage } = {}) {
        return this.sendMessage(
            new RongIMLib.ReferenceMessage({
                user: this.messageUser,
                // 被引用消息的发送用户 Id
                referMsgUserId: referMessage.senderUserId,
                // 引用消息对象
                referMsg: referMessage,
                // 输入的文本消息内容
                content,
                // 引用的消息类型
                objName: MessageType.TEXT
            })
        )
    }

    /**
     * 拉取历史消息
     * @param options.timestamp （可选项）获取此时间之前的消息，0 为从当前时间拉取
     * @returns {Promise<any>}
     */
    async getHistoryList(options) {
        try {
            const data = await callRimApi(
                RongIMLib.getHistoryMessages(this.conversation, {
                    count: 20, // （可选项）获取消息的数量，范围: 1-20
                    order: 0, // （可选项）获取消息的排列顺序，值为 0或1，0为升序，1位降序
                    ...options
                })
            )
            return Promise.resolve(data.list || [])
        } catch (e) {
            return Promise.reject(e)
        }
    }

    /**
     * 撤回消息
     * options.messageUId 消息的UId
     * options.sentTime 消息的发送时间
     * @returns {Promise<unknown>}
     */
    recall(options) {
        return callRimApi(RongIMLib.recallMessage(this.conversation, options))
    }

    /**
     * 删除消息
     * @param messages[0].messageUId
     * @param messages[0].sentTime
     * @param messages[0].messageDirection
     * @returns {Promise<unknown>}
     */
    delete(messages) {
        return callRimApi(RongIMLib.deleteMessages(this.conversation, messages))
    }
}

// 群组生成工厂
export async function groupFactory({ groupId, groupName, messageUser }) {
    if (!RIM.connected) {
        await RIM.getConfigHttp()
        await RIM.connect()
    }

    return new GroupManage({
        groupId,
        groupName,
        messageUser
    })
}
